import { Input } from "antd";
import { useFormik } from 'formik';
import React, { useContext, useState } from "react";
import LazyLoad from "react-lazyload";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from 'yup';
import Logo from "../../assets/images/logo.png";
import LoginPageVideo from "../../assets/videos/login-bg-video.mp4";
import ErrorMessage from '../../components/Forms/ErrorMessage';
import ButtonLoader from "../../components/Loader/ButtonLoader";
import { AppContext } from "../../context/AppContextProvider";
import useAxiosInstance from "../../lib/useAxiosInstance";



const SignIn = () => {
    const navigate = useNavigate();
    const axiosInstance = useAxiosInstance();

    const { setIsLoggedIn, setTheme } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [showEmailLogin, setShowEmailLogin] = useState(true);
    const [showOtpScreen, setShowOtpScreen] = useState(false);
    const [otpResendLoading, setOtpResendLoading] = useState(false);
    const userString = localStorage.getItem("admin-user");
    const user = JSON.parse(userString);

    // OTP state
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpError, setOtpError] = useState("");

    // Form validation schemas
    const EmailFormSchema = Yup.object().shape({
        email: Yup.string().trim().required('Email is required').email('Valid email is required')
            .matches('^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$', 'Valid email is required')
            .min(10, 'Email is minimum 10 character').max(100, 'Email is maximum 100 character'),
        password: Yup.string().trim().required('Password is required').min(6, 'Password is minimum 6 character').max(20, 'Password is maximum 20 character')
    });

    const PhoneFormSchema = Yup.object().shape({
        phone: Yup.string().trim().required('Phone is required')
            .matches(/^\d{10}$/, 'Phone number should be 10 digits'),
        password: Yup.string().trim().required('Password is required').min(6, 'Password is minimum 6 character').max(20, 'Password is maximum 20 character')
    });

    const emailFormik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        enableReinitialize: true,
        validationSchema: EmailFormSchema,
        onSubmit: (values) => {
            handleSubmit(values);
        }
    });

    const phoneFormik = useFormik({
        initialValues: {
            phone: '',
            password: ''
        },
        enableReinitialize: true,
        validationSchema: PhoneFormSchema,
        onSubmit: (values) => {
            handleSubmit(values);
        }
    });

    const handleLoginFormChangeType = () => {
        setShowEmailLogin(!showEmailLogin);
        emailFormik.resetForm();
        phoneFormik.resetForm();
    };

    // OTP input handlers
    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus to next input
        if (value && index < 3) {
            document.getElementById(`otp-input-${index + 1}`).focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        // Handle backspace to move to previous input
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        const reqBody = showEmailLogin ?
            { email: values.email, password: values.password } :
            { phone: values.phone, password: values.password };

        axiosInstance.post("api/users/login", reqBody)
            .then((response) => {
                if (response.data.message === "OTP sent on registered mobile number") {
                    toast.success("OTP sent on registered mobile number");
                    setShowOtpScreen(true);
                } else {
                    setIsLoggedIn(true);
                    localStorage.clear();
                    localStorage.setItem("token", response.data.data.token);
                    localStorage.setItem("admin-user", JSON.stringify(response.data.data.user));

                    // Set theme when navigating to the dashboard
                    setTheme((storedTheme) => storedTheme || "light");
                    if(response.data.data.user.role === 'clientManager')
                        navigate("/news-reports");
                    else
                        navigate("/dashboard");
                }
            })
            .catch((error) => {
                const formik = showEmailLogin ? emailFormik : phoneFormik;
                if (error.response) {
                    const errorMessage = error.response.data.message;
                    const emailErrorMsg = error.response.data.error;

                    if (errorMessage === "This account is suspended.") {
                        formik.setFieldError('password', 'This account is suspended.');
                    }
                    else if (emailErrorMsg === "Can't find User! please enter correct email") {
                        formik.setFieldError('password', "User does not exist.");
                    }
                    else {
                        formik.setFieldError('password', 'Invalid credentials. Please try again.');
                    }
                } else {
                    // Handle other types of errors (e.g., network errors)
                    formik.setFieldError('password', 'Invalid credentials. Please try again.');
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Handle OTP verification
    const handleVerifyOtp = (e) => {
        e.preventDefault();

        const otpValue = otp.join("");
        if (otpValue.length !== 4) {
            setOtpError("Please enter a valid 4-digit OTP");
            return;
        }

        setIsLoading(true);

        let reqBody;
        if (showEmailLogin) {
            reqBody = {
                email: emailFormik.values.email,
                otp: otpValue
            };
        } else {
            reqBody = {
                phone: phoneFormik.values.phone,
                otp: otpValue
            };
        }

        axiosInstance
            .post("api/users/verify-otp-login", reqBody)
            .then((response) => {
                setIsLoggedIn(true);
                localStorage.clear();
                localStorage.setItem("token", response.data.data.token);
                localStorage.setItem("admin-user", JSON.stringify(response.data.data.user));

                // Set theme when navigating to the dashboard
                setTheme((storedTheme) => storedTheme || "light");

                navigate("/dashboard");
            })
            .catch((error) => {
                if (error?.response?.data?.message === "Incorrect OTP. Please try again.") {
                    setOtpError("Incorrect OTP. Please try again.");
                } else if (error?.response?.data?.message === "Timeout. Please try again.") {
                    setOtpError("Timeout. Please try again");
                } else {
                    setOtpError(error?.response?.data?.message || "Something went wrong");
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Handle resend OTP
    const handleResendOtp = () => {
        setOtpResendLoading(true);

        const values = showEmailLogin ? emailFormik.values : phoneFormik.values;
        const reqBody = showEmailLogin ?
            { email: values.email, password: values.password } :
            { phone: values.phone, password: values.password };

        axiosInstance.post("api/users/login", reqBody)
            .then((response) => {
                if (response.data.message === "OTP sent on registered mobile number") {
                    toast.success("OTP sent on registered mobile number");
                }
            })
            .catch((error) => {
                setOtpError(error?.response?.data?.message || "Something went wrong");
            })
            .finally(() => {
                setOtpResendLoading(false);
            });
    };

    // Go back to login screen from OTP screen
    const handleBackToLogin = () => {
        setShowOtpScreen(false);
        setOtp(["", "", "", ""]);
        setOtpError("");
    };

    return (
        <div>
            <ToastContainer />
            <div className='video-background'>
                <LazyLoad height={200} once>
                    <video id='video-bg' autoPlay muted loop>
                        <source src={LoginPageVideo} type='video/mp4' />
                    </video>
                </LazyLoad>
            </div>
            <div className='signup-page grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2'>
                <div className="get-in">
                    <div className='logo'>
                        <img className="block mx-auto" src={Logo} alt="Logo" />
                    </div>
                    <div className="text-content">
                        <h2>Welcome</h2>
                    </div>

                    <div className="sign-in-para">
                        <p>Login to your account details below</p>
                    </div>

                    {!showOtpScreen ? (
                        <form onSubmit={showEmailLogin ? emailFormik.handleSubmit : phoneFormik.handleSubmit}>
                            <div className="flex flex-wrap">
                                {showEmailLogin ? (
                                    <div className="py-2 w-full">
                                        <div className="add-modal-row mt-7 mb-0">
                                            <label className="leading-7 text-lg text-gray-600">
                                                Email
                                            </label>
                                            <Input
                                                type='text'
                                                placeholder='E-mail'
                                                name='email'
                                                className={`w-full rounded-lg text-base py-2 px-3 mt-2`}
                                                onChange={emailFormik.handleChange}
                                                value={emailFormik.values.email}
                                            />
                                            <ErrorMessage
                                                hasError={Boolean(emailFormik.errors.email && emailFormik.touched.email)}
                                                message={emailFormik.errors.email}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-2 w-full">
                                        <div className="add-modal-row mt-7 mb-0">
                                            <label className="leading-7 text-lg text-gray-600">
                                                Phone
                                            </label>
                                            <Input
                                                type='tel'
                                                placeholder='Phone Number'
                                                name='phone'
                                                className={`w-full rounded-lg text-base py-2 px-3 mt-2`}
                                                onChange={phoneFormik.handleChange}
                                                value={phoneFormik.values.phone}
                                            />
                                            <ErrorMessage
                                                hasError={Boolean(phoneFormik.errors.phone && phoneFormik.touched.phone)}
                                                message={phoneFormik.errors.phone}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="py-2 w-full">
                                    <div className="add-modal-row mt-3 mb-0">
                                        <label className="leading-7 text-lg text-gray-600">
                                            Password
                                        </label>
                                        <Input.Password
                                            type='password'
                                            placeholder='Password'
                                            name='password'
                                            className={`w-full rounded-lg text-base py-2 px-3 mt-2 signin-password`}
                                            onChange={showEmailLogin ? emailFormik.handleChange : phoneFormik.handleChange}
                                            value={showEmailLogin ? emailFormik.values.password : phoneFormik.values.password}
                                        />
                                        <ErrorMessage
                                            hasError={Boolean(
                                                (showEmailLogin ?
                                                    (emailFormik.errors.password && emailFormik.touched.password) :
                                                    (phoneFormik.errors.password && phoneFormik.touched.password))
                                            )}
                                            message={showEmailLogin ? emailFormik.errors.password : phoneFormik.errors.password}
                                        />
                                    </div>
                                </div>

                                <div className="w-full flex justify-between my-2">
                                    <div
                                        onClick={handleLoginFormChangeType}
                                        className="forgot-pass-text hover:cursor-pointer"
                                    >
                                        {showEmailLogin ? "Login With Phone" : "Login With Email"}
                                    </div>
                                    <Link to="/login/forgot-password" className="forgot-pass-text">
                                        Forgot Password?
                                    </Link>
                                </div>

                                <div className="py-2 w-full">
                                    <div className="my-4">
                                        <button
                                            className="flex mx-auto w-full text-white bg-sky-900 border-0 py-3 justify-center focus:outline-none rounded-lg text-lg"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <ButtonLoader /> : 'Login'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    ) : (
                        // OTP Screen
                        <form onSubmit={handleVerifyOtp}>
                            <div className="flex flex-wrap">
                                <div className="py-2 w-full">
                                    <div className="mt-3">
                                        <label className="leading-7 text-lg text-gray-600">
                                            Enter 4-digit OTP
                                        </label>
                                        <div className="flex gap-8 mt-2">
                                            {[0, 1, 2, 3].map((index) => (
                                                <input
                                                    key={index}
                                                    type="text"
                                                    id={`otp-input-${index}`}
                                                    maxLength="1"
                                                    value={otp[index]}
                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    className="w-16 h-16 text-center text-xl font-bold rounded-lg border-2 border-gray-300 focus:border-sky-900 focus:outline-none"
                                                />
                                            ))}
                                        </div>
                                        {otpError && (
                                            <div className="text-red-500 text-sm mt-1">{otpError}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="w-full flex justify-between my-2">
                                    <button
                                        type="button"
                                        onClick={handleBackToLogin}
                                        className="text-sky-900 hover:underline"
                                    >
                                        <span className="mr-1">←</span>
                                        Back to Login
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={otpResendLoading}
                                        className="text-sky-900 hover:underline"
                                    >
                                        {otpResendLoading ? "Resending..." : "Resend OTP"}
                                    </button>
                                </div>

                                <div className="py-2 w-full">
                                    <div className="my-4">
                                        <button
                                            className="flex mx-auto w-full text-white bg-sky-900 border-0 py-3 justify-center focus:outline-none rounded-lg text-lg"
                                            type="submit"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <ButtonLoader /> : 'Verify OTP'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignIn;